import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  getRedirectResult,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { create } from 'zustand';
import { auth } from '@/lib/firebase';
import { env } from '@/lib/env';
import { tStatic } from '@/i18n';

const EMAIL_FOR_LINK_KEY = 'once.magicLinkEmail';

interface AuthState {
  user: User | null;
  /** True until the first onAuthStateChanged callback lands. */
  loading: boolean;
  /**
   * Set when a Google sign-in that fell back to a full-page redirect (see
   * `signInWithGoogle`) comes back having failed. That redirect tears the
   * SPA down and reloads it, so by the time the result is available there is
   * no component left in memory to show it - the only place to catch it is
   * here, at startup, in `init()`.
   */
  redirectError: unknown | null;
  clearRedirectError: () => void;
  init: () => () => void;

  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLink: (href: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  /** Anonymous identity used to bind a viewer session to one browser. */
  ensureAnonymous: () => Promise<User>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  redirectError: null,

  init: () => {
    // When sign-in fell back to a redirect, the app reloads on the way back
    // and the result is waiting here. `getRedirectResult` resolves to `null`
    // - it does not throw - when there simply was no pending redirect, so a
    // rejection here is always a genuine failure of one that did happen, and
    // onAuthStateChanged alone would never explain why the user is still
    // signed out. That used to be swallowed silently; on a phone, with no
    // console, that's a Google sign-in button that just quietly does
    // nothing forever.
    void getRedirectResult(auth).catch((error) => set({ redirectError: error }));

    return onAuthStateChanged(auth, (user) => {
      set({ user, loading: false });
    });
  },

  clearRedirectError: () => set({ redirectError: null }),

  signUp: async (email, password) => {
    await createUserWithEmailAndPassword(auth, email, password);
  },

  signIn: async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  },

  signInWithGoogle: async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    // Popup first, everywhere, including touch devices. This used to go
    // straight to a redirect on touch devices instead, on the theory that a
    // popup on mobile is a second tab some browsers open and then never
    // deliver a result from. That traded one failure for a worse one: since
    // browsers started partitioning or blocking third-party storage by
    // default (Chrome 115+, Safari 16.1+, Firefox 109+), a redirect whose
    // Auth domain isn't the app's own origin can no longer resolve on the
    // way back at all - it surfaces as a bare `auth/internal-error`, with
    // nothing left to retry. A popup has no such dependency: it reports the
    // result straight back to this page via postMessage, not by re-reading
    // storage across a domain boundary. It stays the fallback, not the
    // primary, for the cases only it covers - blocked outright, or refused
    // by an in-app webview.
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      // The result is picked up by getRedirectResult() at startup - see
      // init() - so nothing else needs to know which path was taken.
      const code = (error as { code?: string } | null)?.code;
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/operation-not-supported-in-this-environment'
      ) {
        await signInWithRedirect(auth, provider);
        return;
      }
      throw error;
    }
  },

  sendMagicLink: async (email) => {
    await sendSignInLinkToEmail(auth, email, {
      url: `${env.VITE_PUBLIC_ORIGIN}/auth/callback`,
      handleCodeInApp: true,
    });
    try {
      // Remembering the address avoids re-prompting on the same device; the
      // link alone is not sufficient to sign in without it.
      localStorage.setItem(EMAIL_FOR_LINK_KEY, email);
    } catch {
      /* storage blocked - the callback screen will ask for the address */
    }
  },

  completeMagicLink: async (href) => {
    if (!isSignInWithEmailLink(auth, href)) return false;
    let email: string | null = null;
    try {
      email = localStorage.getItem(EMAIL_FOR_LINK_KEY);
    } catch {
      /* handled below */
    }
    if (!email) {
      email = window.prompt(tStatic('auth.confirmEmailPrompt'));
    }
    if (!email) return false;

    await signInWithEmailLink(auth, email, href);
    try {
      localStorage.removeItem(EMAIL_FOR_LINK_KEY);
    } catch {
      /* nothing to clean up */
    }
    return true;
  },

  resetPassword: async (email) => {
    await sendPasswordResetEmail(auth, email, {
      url: `${env.VITE_PUBLIC_ORIGIN}/auth/sign-in`,
    });
  },

  ensureAnonymous: async () => {
    const current = get().user ?? auth.currentUser;
    if (current) return current;
    const credential = await signInAnonymously(auth);
    return credential.user;
  },

  logout: async () => {
    await signOut(auth);
  },

  deleteAccount: async () => {
    const user = auth.currentUser;
    if (!user) throw new Error(tStatic('apiError.unauthenticated'));
    // Server-side cleanup (videos, links, storage) is driven by the
    // onUserDeleted trigger; the client only removes the auth record.
    await deleteUser(user);
  },
}));
